import { ampli } from 'ampli';

import { vettingPath } from '../common-flow/entries/path';
import {
  type NONE_COUNTRY_CODE,
  type SocialMediaAccountLinks,
  type CharityTypeEnum,
  type CharityVettingFormValues,
  type CharityVettingUrlTypeValues,
  type ConsumptionTypeEnum,
  type CountryCode,
  type DonationPreferencesEnum,
  type UnwantedFoodType,
} from '../types/charityVettingTypes';

export type SectionStepName =
  | 'registration'
  | 'contact-details'
  | 'donation-preferences'
  | 'social-accounts'
  | 'pli'
  | 'unwanted-food'
  | 'food-hygiene';

type AmpliEventWrapper = {
  eventName: string;
  countryIsoCode: CountryCode | typeof NONE_COUNTRY_CODE;
  data?: {
    charityType?: CharityTypeEnum;
    fiscalBenefitsArticle?: boolean;
    donationPreferences?: DonationPreferencesEnum;
    charity_article_62?: boolean;
    type_donation?: DonationPreferencesEnum;
    accepts_all_clicked?: boolean;
    preparationKitchen?: boolean;
    foodFor?: ConsumptionTypeEnum;
    follows_haccp?: boolean;
    section?: SectionStepName | CharityVettingUrlTypeValues;
    contactUsSection?: string;
    screenQuestion?: string;
  };
};

const mapUnwantedFoodActiveStateAmpliEvent = (
  unwantedFoodItems: UnwantedFoodType[],
  activeItem: string,
): boolean => {
  const unwantedFoodActiveItem = unwantedFoodItems.find(
    (item) => item.active && item.code === activeItem,
  );
  return !!unwantedFoodActiveItem?.active;
};

const mapUnwantedFoodItemsToAmpliEvent = (
  unwantedFoodItems: UnwantedFoodType[],
  additionalUnwantedFoodCategories: UnwantedFoodType[],
) => ({
  bakery: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'BAKERY'),
  chilled_food: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'CHILLED_FOOD'),
  fish: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'FISH'),
  frozen_food: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'FROZEN_FOOD'),
  fruits_vegetables: mapUnwantedFoodActiveStateAmpliEvent(
    unwantedFoodItems,
    'FRUITS_AND_VEGETABLES',
  ),
  meat: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'MEAT'),
  baby_food: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'BABY'),
  raw_meat: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'RAW_MEAT'),
  raw_fish: mapUnwantedFoodActiveStateAmpliEvent(unwantedFoodItems, 'RAW_FISH'),
  other: additionalUnwantedFoodCategories?.length > 0,
});

export const handleStepNameForAmpliEvents = (route = '') => route.split('/')?.pop() ?? '';

export const ampliEventsMapper = ({ eventName, countryIsoCode, data }: AmpliEventWrapper) => {
  switch (eventName) {
    case 'screenCharityOnboardingWelcome':
      ampli.screenCharityOnboardingWelcome({ charity_country: countryIsoCode });
      break;
    case 'actionCharityOnboardingInitiateSurvey':
      ampli.actionCharityOnboardingInitiateSurvey({ charity_country: countryIsoCode });
      break;
    case 'charity_onboarding_contact_charity_name_selected':
      ampli.charityOnboardingContactCharityNameSelected({
        charity_country: countryIsoCode,
      });
      break;
    case 'charity_onboarding_contact_charity_not_found_clicked':
      ampli.charityOnboardingContactCharityNotFoundClicked({
        charity_country: countryIsoCode,
      });
      break;
    case 'screen_charity_onboarding_social_media':
      ampli.screenCharityOnboardingSocialMedia({ charity_country: countryIsoCode });
      break;
    case 'screen_charity_onboarding_review':
      ampli.screenCharityOnboardingReview({ charity_country: countryIsoCode });
      break;
    case 'charity_onboarding_review_edit_section_clicked':
      ampli.charityOnboardingReviewEditSectionClicked({
        charity_country: countryIsoCode,
        section_to_edit: data?.section as SectionStepName,
      });
      break;
    case 'screen_charity_onboarding_finished':
      ampli.screenCharityOnboardingFinished({ charity_country: countryIsoCode });
      break;
    case 'charity_onboarding_contact_us_clicked':
      ampli.charityOnboardingContactUsClicked({
        charity_country: countryIsoCode,
        contact_us_section: data?.contactUsSection ?? '',
      });
      break;
    case 'charity_onboarding_help_button_clicked':
      ampli.charityOnboardingHelpButtonClicked({
        charity_country: countryIsoCode,
        screen_question: data?.screenQuestion ?? '',
      });
      break;
    case 'charity_onboarding_back_button_clicked':
      ampli.charityOnboardingBackButtonClicked({
        charity_country: countryIsoCode,
        section: data?.section as CharityVettingUrlTypeValues,
      });
      break;
    default:
      break;
  }
};

export const socialMediaLinksProvided = (
  socialMediaAccountLinks: SocialMediaAccountLinks[],
): boolean => socialMediaAccountLinks.some((link) => link.link !== '');

export const triggerAmpliEventOnNextRoute = (
  stepName: string,
  countryIsoCode: CountryCode,
  data?: CharityVettingFormValues['charityOnboarding'],
) => {
  const {
    socialMediaAccountLinks = [],
    unwantedFoodItems = [],
    additionalUnwantedFoodCategories = [],
    acceptsAllFood = false,
    fiscalBenefitsArticle = false,
    consumptionType,
    donationPreferences,
    preparationKitchen = false,
    haccp = false,
    hasRgseaa = false,
    donationCertificate = false,
    charityType,
    nip,
    hasPli = false,
  } = data ?? {};
  switch (stepName) {
    // Shared events for shared steps
    case vettingPath.unwantedFoodPath:
      ampli.charityOnboardingDonationPreferenceFoodUnwanted({
        charity_country: countryIsoCode,
        ...mapUnwantedFoodItemsToAmpliEvent(unwantedFoodItems, additionalUnwantedFoodCategories),
      });
      break;
    case vettingPath.contactDetailsPath:
      ampli.charityOnboardingContactConfirmClicked({ charity_country: countryIsoCode });
      break;
    case vettingPath.manualContactDetailsPath:
      ampli.actionCharityOnboardingContactManualConfirmClicked({ charity_country: countryIsoCode });
      break;
    case vettingPath.searchPath:
      ampli.charityOnboardingContactCharityNameSelected({ charity_country: countryIsoCode });
      break;
    case vettingPath.acceptAllFoodsPath:
      ampli.charityOnboardingFoodUnwantedAcceptsAllClicked({
        accepts_all_clicked: acceptsAllFood,
        charity_country: countryIsoCode,
      });
      break;
    case vettingPath.socialAccountsPath:
      ampli.actionCharityOnboardingSocialMediaNextClicked({
        charity_country: countryIsoCode,
        any_social_media_provided: socialMediaLinksProvided(socialMediaAccountLinks),
      });
      break;
    case vettingPath.reviewDetailsPath:
      ampli.charityOnboardingSubmitClicked({ charity_country: countryIsoCode });
      break;
    // Shared events
    case vettingPath.nipPath:
    case vettingPath.donationCertificatePath:
    case vettingPath.fiscalBenefitsPath:
    case vettingPath.pliPath:
      ampli.charityOnboardingRegistrationFulfillLegalRequirements({
        charity_country: countryIsoCode,
        charity_fulfill_legal_requirements:
          !!nip || hasPli || fiscalBenefitsArticle || donationCertificate,
      });
      break;
    case vettingPath.charityNumberPath:
    case vettingPath.cifPath:
    case vettingPath.nifPath:
    case vettingPath.sirenPath:
    case vettingPath.krsPath:
      ampli.charityOnboardingRegistrationNumber({ charity_country: countryIsoCode });
      break;
    // Shared events between ES & PT
    case vettingPath.registrationPath:
      ampli.charityOnboardingRegistrationTypeCharity({
        charity_country: countryIsoCode,
        charity_type: charityType ?? '',
      });
      break;
    case vettingPath.donationPreferencesPath:
      ampli.charityOnboardingDonationPreferenceTypeDonation({
        charity_country: countryIsoCode,
        type_donation: donationPreferences ?? '',
      });
      break;
    case vettingPath.consumptionTypePath:
      ampli.charityOnboardingDonationPreferenceFoodForHumanOrAnimal({
        charity_country: countryIsoCode,
        food_for: consumptionType ?? '',
      });
      break;
    case vettingPath.preparationKitchenPath:
      ampli.charityOnboardingDonationPreferenceHasKitchen({
        charity_country: countryIsoCode,
        has_kitchen: preparationKitchen,
      });
      break;
    case vettingPath.haccpPath:
      ampli.charityOnboardingDonationPreferenceHaccp({
        charity_country: countryIsoCode,
        follows_haccp: haccp,
      });
      break;
    // Country specific events for PT
    case vettingPath.nipcPath:
      ampli.charityOnboardingRegistrationNipcNumber({ charity_country: countryIsoCode });
      break;
    // Country specific events for ES
    case vettingPath.rgseaaQuestionPath:
      ampli.charityOnboardingDonationPreferenceHasFoodSafetyRegistry({
        charity_country: countryIsoCode,
        has_food_safety_registry: hasRgseaa,
      });
      break;
    case vettingPath.rgseaaPath:
      ampli.charityOnboardingDonationPreferenceFoodSafetyRegistryNumber({
        charity_country: countryIsoCode,
      });
      break;
  }
};
